import { CharacterRepository } from '../persistence/characterRepository.ts';
import { SessionRepository } from '../persistence/sessionRepository.ts';
import { CheckpointCommitter } from '../persistence/checkpointCommitter.ts';
import { RuntimeRepository } from '../persistence/runtimeRepository.ts';
import { CustomSkillRepository } from '../persistence/customSkillRepository.ts';
import { CustomBackgroundRepository } from '../persistence/customBackgroundRepository.ts';
import { CustomItemRepository } from '../persistence/customItemRepository.ts';
import { createMemoryStorage } from '../persistence/memoryStorage.ts';
import type { StoragePort } from '../persistence/storagePort.ts';

/**
 * Composition root for any host (Owlbear extension, Obsidian GM Console, tests).
 *
 * The desktop app decided here between Electron files and memory. The shared
 * engine takes the storage from its host instead: the Owlbear extension passes
 * the sheet-server storage, tests pass nothing and get memory.
 */
export interface AppServices {
  repository: CharacterRepository;
  sessions: SessionRepository;
  checkpoints: CheckpointCommitter;
  runtime: RuntimeRepository;
  /** The player's own definitions. Catalogue content, not per-crawler. */
  customSkills: CustomSkillRepository;
  customBackgrounds: CustomBackgroundRepository;
  customItems: CustomItemRepository;
  storage: StoragePort;
  /** False only for the in-memory fallback, whose data is lost on reload. */
  persistent: boolean;
}

export function createServices(
  storage?: StoragePort,
  now: () => string = () => new Date().toISOString(),
): AppServices {
  const store = storage ?? createMemoryStorage();
  const repository = new CharacterRepository(store);
  const sessions = new SessionRepository(store);
  const runtime = new RuntimeRepository(store);
  return {
    repository,
    sessions,
    checkpoints: new CheckpointCommitter(store, repository, sessions, now, runtime),
    runtime,
    customSkills: new CustomSkillRepository(store),
    customBackgrounds: new CustomBackgroundRepository(store),
    customItems: new CustomItemRepository(store),
    storage: store,
    persistent: storage !== undefined,
  };
}
